import type { Game } from '../core/game';
import * as SKILLTREE_MODULE from '../core/skilltree';
import { NODE_BY_ID } from '../data/nodes';

/**
 * Adaptateur « arbre de talents » — COMMENTS v0.4.1 §1.2 et §2.
 *
 * `describeNodeLevel(id, level) → { current, next }` est livré par l'agent
 * `src/core` en parallèle : il est cherché à l'exécution sur la façade `Game`
 * **puis** dans `core/skilltree` (import d'espace de noms : aucune liaison
 * statique sur un nom qui n'existe pas encore). Sans lui, l'interface retombe
 * sur `NodeDef.description`, comme en v0.4.x.
 */

export interface NodeLevelText {
  /** Ce que le nœud apporte **au niveau possédé** (« Aucun bonus » au niveau 0). */
  current: string;
  /** Ce qu'apporterait le niveau suivant. */
  next: string;
}

type Describe = (id: string, level: number) => unknown;

function resolveDescribe(game: Game): Describe | null {
  const fromGame = (game as unknown as Record<string, unknown>).describeNodeLevel;
  if (typeof fromGame === 'function') return (fromGame as Describe).bind(game);
  const fromModule = (SKILLTREE_MODULE as unknown as Record<string, unknown>).describeNodeLevel;
  if (typeof fromModule === 'function') return fromModule as Describe;
  return null;
}

/** Le cœur décrit-il les niveaux de nœuds (info-bulles §2) ? */
export function hasNodeDescriptions(game: Game): boolean {
  return resolveDescribe(game) !== null;
}

/**
 * Textes d'info-bulle d'un nœud. `null` tant que le cœur ne les fournit pas —
 * l'appelant affiche alors la description statique du nœud.
 */
export function describeNodeLevel(game: Game, id: string, level: number): NodeLevelText | null {
  const fn = resolveDescribe(game);
  if (!fn) return null;
  let out: unknown;
  try {
    out = fn(id, level);
  } catch {
    return null;
  }
  if (typeof out !== 'object' || out === null) return null;
  const o = out as Record<string, unknown>;
  const current = typeof o.current === 'string' ? o.current : '';
  const next = typeof o.next === 'string' ? o.next : '';
  if (!current && !next) return null;
  return { current, next };
}

/**
 * Profondeur d'un nœud dans l'arbre (racine = 0). `core/skilltree.nodeTier` si
 * le cœur l'expose, sinon remontée locale des `parent`. Sert à ordonner les
 * colonnes de l'écran Arbre : un parent est toujours au-dessus de ses enfants.
 */
export function nodeDepth(id: string): number {
  const tier = (SKILLTREE_MODULE as unknown as Record<string, unknown>).nodeTier;
  if (typeof tier === 'function') {
    try {
      const n = (tier as (id: string) => number)(id);
      if (Number.isFinite(n)) return n;
    } catch {
      /* repli local */
    }
  }
  let depth = 0;
  let def = NODE_BY_ID[id] as { parent?: string | null } | undefined;
  if (!def) return 0;
  while (def?.parent) {
    depth += 1;
    def = NODE_BY_ID[def.parent] as { parent?: string | null } | undefined;
    if (!def || depth > 32) break;
  }
  return depth;
}
